import type { ISchemaBoundValidationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { newCid } from '@/features/legends/editor/collections'
import type { SymbolContext } from '@/features/legends/editor/entities'
import type { FilterScope } from './filter-scope'
import type { KnownRule } from './rules'

export interface FilterEditorViewModelOptions {
  dependencies: LegendPartDependencies
  scope: readonly FilterScope[]
  context: SymbolContext
}

/**
 * Base for filter editors. A filter is a condition: questions use them to
 * decide when they show, routing uses them to match pros. Each type validates
 * itself with its own schema.
 */
export abstract class FilterEditorViewModelBase<
  TRule extends KnownRule = KnownRule,
> extends LegendPartViewModelBase {
  protected readonly scope: readonly FilterScope[]
  protected readonly context: SymbolContext

  /**
   * Identifies the filter on the client, for keys.
   */
  readonly cid = newCid('filter')

  /**
   * The filter type, as stored in the legend.
   */
  abstract readonly type: TRule['type']

  /**
   * The filter's validation, bound to its type's schema.
   */
  abstract readonly validation: ISchemaBoundValidationContext<FilterEditorViewModelBase>

  constructor({ dependencies, scope, context }: FilterEditorViewModelOptions) {
    super(dependencies)
    this.scope = scope
    this.context = context
  }

  /**
   * The options for the filter's own sub-filters, which share its scope and
   * context.
   */
  protected get _filterOptions(): FilterEditorViewModelOptions {
    return {
      dependencies: this.dependencies,
      scope: this.scope,
      context: this.context,
    }
  }

  /**
   * Sets the filter from the legend document.
   */
  abstract hydrate(rule: TRule): void

  /**
   * The filter as the legend document stores it.
   */
  abstract toJSON(): TRule

  /**
   * Shows the analyzer's errors for the filter.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    receiveErrors(this, descriptor.inner)
  }
}
